import { useEffect, useRef, useState } from "react";
import type { Worker } from "tesseract.js";
import { parseContactScan, ScannedContact } from "./parseContactScan";

export default function ContactScan({ onRecognized, onScanningChange }: {
    onRecognized: (values: ScannedContact) => void;
    onScanningChange?: (busy: boolean) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [complete, setComplete] = useState(false);
    const workerRef = useRef<Worker | null>(null);
    const runRef = useRef(0);

    useEffect(() => {
        onScanningChange?.(busy);
        return () => onScanningChange?.(false);
    }, [busy, onScanningChange]);

    useEffect(() => () => {
        runRef.current += 1;
        const worker = workerRef.current;
        workerRef.current = null;
        if (worker) void worker.terminate().catch(() => {});
    }, []);

    async function scan(file: File) {
        if (busy) return;
        setError("");
        if (!/^image\/(jpeg|png|webp|bmp)$/i.test(file.type)) {
            setError("Choose a JPG, PNG, WebP, or BMP image.");
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            setError("Choose an image smaller than 20 MB.");
            return;
        }
        const run = ++runRef.current;
        setBusy(true);
        setStatus("Preparing scanner…");
        let worker: Worker | undefined;
        try {
            const { createWorker } = await import("tesseract.js");
            if (run !== runRef.current) return;
            worker = await createWorker("eng", 1, {
                logger: message => {
                    if (run === runRef.current) setStatus(message.status === "recognizing text"
                        ? `Reading text… ${Math.round(message.progress * 100)}%`
                        : "Preparing scanner…");
                },
            });
            if (run !== runRef.current) return;
            workerRef.current = worker;
            const { data } = await worker.recognize(file);
            if (run !== runRef.current) return;
            if (!data.text.trim()) {
                setError("No text found. Try a clearer, well-lit photo of the card.");
                setStatus("");
                return;
            }
            onRecognized(parseContactScan(data.text));
            setComplete(true);
            setStatus("Scan complete. Review the suggested details below before adding the contact. The full scanned text is in Background.");
        } catch {
            if (run === runRef.current) {
                setError("Could not scan this image. Check your connection and try a clear JPG or PNG photo.");
                setStatus("");
            }
        } finally {
            if (worker && (workerRef.current === worker || run !== runRef.current)) {
                if (workerRef.current === worker) workerRef.current = null;
                await worker.terminate().catch(() => {});
            }
            if (run === runRef.current) setBusy(false);
        }
    }

    return (
        <section className="border rounded p-3 my-3" aria-label="Scan contact" aria-busy={busy}>
            <label className="fw-bold form-label" htmlFor="contactScanImage">Scan a business card</label>
            <p className="text-muted">Choose a clear photo of an English business card. Review the results and select the organization before saving.</p>
            {!complete && <input id="contactScanImage" type="file" className="form-control"
                accept="image/jpeg,image/png,image/webp,image/bmp" disabled={busy}
                onChange={event => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) void scan(file);
                }}/>}
            <div role="status" className="mt-2">{status}</div>
            {error && <div role="alert" className="text-danger mt-2">{error}</div>}
        </section>
    );
}
